import { ListTodo, Power, Search, User, X } from "lucide-react";
import {
  Box,
  Divider,
  Drawer,
  InputAdornment,
  LinearProgress,
  List,
  ListItem,
  ListItemButton,
  TextField,
  Typography,
  useTheme,
} from "@mui/material";
import { useMemo, useState } from "react";
import { Link, NavLink } from "react-router-dom";
import { useAppSelector } from "../../app/hooks";
import Logo from "@/shared/ui/Logo";
import config from "../../config/config";
import { getKillLaunchCountdown } from "@/features/kill-initiation";
import type { DetectiveSidebarBadge } from "@/features/detective-agency";
import { getVaultPayoutCountdown } from "@/features/vault";
import { useTermsDialog } from "@/features/terms";
import { City, GAME_CASH_TRANSFER_COOLDOWN } from "../../constants/const";
import { JAIL_RESTRICTED_PATHS, SAFEHOUSE_RESTRICTED_PATHS } from "../../constants/jail";
import {
  selectNextSafehouseTime,
  selectSafehouseUntil,
} from "@/features/safehouse";
import { SideNavLinks } from "../../constants/page";
import useCurrentTime from "../../hook/useCurrentTime";
import useWallet from "../../hook/useWallet";
import {
  convertDateTimeWithMinutes,
  formatNumberWithComma,
  shortAddress,
} from "../../utils/utils";
import useLotteryHallSidebar from "../../hook/useLotteryHallSidebar";
import millify from "millify";
import { selectJailedUntil } from "@/features/jail";
import { useStyles } from "./SideBar.styles";
import { selectNextRaceTime } from "@/features/race";
import { selectNextTrainTime } from "@/features/kill-skill";
import { selectNextCrimeTime } from "@/features/crime";
import { selectNextNickTime } from "@/features/nick-car";
import { selectNextCrushTime } from "@/features/car-crusher";
import { selectWeeklyMissionSummary } from "@/features/story";

const MobileSideBar = ({
  detectiveBadge,
  showMobileSideBar,
  toggleMobileSideBar,
  onConnectWallet,
}: {
  detectiveBadge: DetectiveSidebarBadge;
  showMobileSideBar: boolean;
  toggleMobileSideBar: React.Dispatch<React.SetStateAction<boolean>>;
  onConnectWallet: () => void;
}) => {
  const { classes } = useStyles();
  const theme = useTheme();
  const currentTime = useCurrentTime();
  const { disconnectWallet, account, isConnected } = useWallet();
  const { openTerms } = useTermsDialog();
  const [searchQuery, setSearchQuery] = useState("");
  const { myProfile } = useAppSelector((state) => state.profile);
  const { cashBalance } = useAppSelector((state) => state.bank);
  const { helperCreditBalance } = useAppSelector((state) => state.helperBot);
  const { giCreditBalance } = useAppSelector((state) => state.gameIntelligence);
  const { bulletBalance } = useAppSelector((state) => state.bulletFactory);
  const { healthBalance } = useAppSelector((state) => state.hospital);
  const { userTravelInfo } = useAppSelector((state) => state.travel);

  const { activePerksInfo: activePerks } = useAppSelector(
    (state) => state.perk
  );

  const { citiesTotalPower, citiesTotalDefense } = useAppSelector(
    (state) => state.equipment
  );

  const lastTransferTime = useAppSelector((state) => state.bank.lastTransferTime);

  const {
    userCarBotInfo,
    userBoozeBotInfo,
    userCrimeBotInfo,
    userKSBotInfo,
    userNarcsBotInfo,
    userBulletBotInfo
  } = useAppSelector((state) => state.helperBot);

  const nextHealthBuyTime = useAppSelector(
    (state) => state.hospital.nextHealthBuyTime
  );

  const nextBulletBuyTime = useAppSelector(
    (state) => state.bulletFactory.nextBulletBuyTime
  );

  const { nextNarcsTime, nextBoozeTime } = useAppSelector(
    (state) => state.smuggling
  );

  const nextRaceTime = useAppSelector(selectNextRaceTime);
  const nextTrainTime = useAppSelector(selectNextTrainTime);
  const nextCrimeTime = useAppSelector(selectNextCrimeTime);
  const nextNickTime = useAppSelector(selectNextNickTime);
  const nextCrushTime = useAppSelector(selectNextCrushTime);
  const jailedUntil = useAppSelector(selectJailedUntil);
  const { status: weeklyMissionStatus, progress: weeklyMissionProgress } =
    useAppSelector(selectWeeklyMissionSummary);

  const { nextLobbyTime } = useAppSelector((state) => state.organizedCrime);

  const nextSafehouseEnterTime = useAppSelector(selectNextSafehouseTime);
  const safehouseUntil = useAppSelector(selectSafehouseUntil);

  const { currentRound } = useAppSelector((state) => state.jackpot);

  const { nextBuyTime } = useAppSelector((state) => state.shop);

  const { info: lotterySidebar } = useLotteryHallSidebar();

  const remainTimes: Record<string, any[]> = useMemo(() => {
    return {
      Crimes: [
        nextCrimeTime > currentTime
          ? convertDateTimeWithMinutes(nextCrimeTime - currentTime)
          : "Now",
        userCrimeBotInfo.isRunning ? "Bot" : "",
      ],

      "Nick a car": [
        nextNickTime > currentTime
          ? convertDateTimeWithMinutes(nextNickTime - currentTime)
          : "Now",
        userCarBotInfo.isRunning ? "Bot" : "",
      ],

      Travel: [
        userTravelInfo
          ? userTravelInfo?.travelUntil > currentTime
            ? convertDateTimeWithMinutes(
              userTravelInfo?.travelUntil - currentTime
            )
            : "Now"
          : "Now",
      ],
      Jail: [
        jailedUntil > currentTime
          ? convertDateTimeWithMinutes(jailedUntil - currentTime)
          : "Now",
      ],
      "Helper bots": [""],
      "Kill skill": [
        nextTrainTime > currentTime
          ? convertDateTimeWithMinutes(nextTrainTime - currentTime)
          : "Now",
        userKSBotInfo.isRunning ? "Bot" : "",
      ],
      "Kill initiation": [getKillLaunchCountdown(currentTime * 1000).compact],
      "Detective Agency": [detectiveBadge ?? ""],
      "Vault countdown": [getVaultPayoutCountdown(currentTime * 1000)],
      Narcotics: [
        nextNarcsTime > currentTime
          ? convertDateTimeWithMinutes(nextNarcsTime - currentTime)
          : "Now",
        userNarcsBotInfo.isRunning ? "Bot" : "",
      ],
      Booze: [
        nextBoozeTime > currentTime
          ? convertDateTimeWithMinutes(nextBoozeTime - currentTime)
          : "Now",
        userBoozeBotInfo.isRunning ? "Bot" : "",
      ],
      "Bullet Factory": [
        nextBulletBuyTime > currentTime
          ? convertDateTimeWithMinutes(nextBulletBuyTime - currentTime)
          : "Now",
        userBulletBotInfo.isRunning ? "Bot" : "",
      ],
      Bank: [
        lastTransferTime + GAME_CASH_TRANSFER_COOLDOWN > currentTime
          ? convertDateTimeWithMinutes(
            lastTransferTime + GAME_CASH_TRANSFER_COOLDOWN - currentTime
          )
          : "Now",
      ],
      Hospital: [
        nextHealthBuyTime > currentTime
          ? convertDateTimeWithMinutes(nextHealthBuyTime - currentTime)
          : "Now",
        "",
      ],
      Racing: [
        nextRaceTime > currentTime
          ? convertDateTimeWithMinutes(nextRaceTime - currentTime)
          : "Now",
        "",
      ],
      Jackpot: [
        currentRound.liveTime + currentRound.duration > currentTime
          ? convertDateTimeWithMinutes(
            currentRound.liveTime + currentRound.duration - currentTime
          )
          : "Now",
        "",
      ],
      "Lottery": [
        lotterySidebar.roundId ? (
          lotterySidebar.endTime > 0 ? (
            <span style={{ color: theme.design.color.status.warning }}>
              {millify(Math.floor(lotterySidebar.entriesCash || 0), { precision: 2 }) + " cash"}
            </span>
          ) : (
            <span style={{ color: theme.design.color.status.warning }}>
              {millify(Math.floor(lotterySidebar.entriesCash || 0), { precision: 2 }) +
                " cash  Waiting"}
            </span>
          )
        ) : (
          "Now"
        ),
        "",
      ],
      Shop: [
        nextBuyTime > currentTime
          ? convertDateTimeWithMinutes(nextBuyTime - currentTime)
          : "Now",
        "",
      ],
      "Organized Crime": [
        nextLobbyTime > currentTime
          ? convertDateTimeWithMinutes(nextLobbyTime - currentTime)
          : "Now",
        "",
      ],
      "Car Crusher": [
        nextCrushTime > currentTime
          ? convertDateTimeWithMinutes(nextCrushTime - currentTime)
          : "Now",
        "",
      ],
      Safehouse: [
        nextSafehouseEnterTime > 0 && nextSafehouseEnterTime > currentTime
          ? convertDateTimeWithMinutes(nextSafehouseEnterTime - currentTime)
          : "Now",
        "",
      ],
    };
  }, [
    currentTime,
    detectiveBadge,
    userCrimeBotInfo.isRunning,
    nextNickTime,
    userCarBotInfo.isRunning,
    userTravelInfo,
    jailedUntil,
    nextTrainTime,
    userKSBotInfo.isRunning,
    nextNarcsTime,
    userNarcsBotInfo.isRunning,
    nextBoozeTime,
    userBoozeBotInfo.isRunning,
    userBulletBotInfo.isRunning,
    lastTransferTime,
    currentRound.liveTime,
    currentRound.duration,
    lotterySidebar.roundId,
    lotterySidebar.endTime,
    lotterySidebar.entriesCash,
    nextBuyTime,
    nextLobbyTime,
    nextCrushTime,
    nextSafehouseEnterTime,
  ]);

  // Filter navigation links based on search query
  const filteredNavLinks = useMemo(() => {
    if (!searchQuery.trim()) {
      return SideNavLinks;
    }

    const query = searchQuery.toLowerCase().trim();

    return SideNavLinks.map((navLink) => ({
      ...navLink,
      subLinks: (navLink.subLinks as any[]).filter(
        (link: any) =>
          link.name.toLowerCase().includes(query) ||
          navLink.category.toLowerCase().includes(query)
      ),
    })).filter((navLink) => navLink.subLinks.length > 0);
  }, [searchQuery]);

  const isInJail = jailedUntil > currentTime;
  const isInSafehouse = safehouseUntil > 0 && currentTime < safehouseUntil;

  const balanceList = [
    {
      tooltip: "GI Credit",
      name: "GI Credit",
      balance: giCreditBalance,
      link: `/gicredit/history`,
      icon: "/assets/imgs/inventory/gicredititem.png",
    },
    {
      tooltip: "Cash History",
      name: "Cash",
      balance: cashBalance,
      link: `/cash/history`,
      icon: "/assets/imgs/layout/currency.png",
    },
    {
      tooltip: "Credit History",
      name: "Credits",
      balance: helperCreditBalance,
      link: `/credit/history`,
      icon: "/assets/imgs/inventory/helperitem.png",
    },
    {
      tooltip: "Bullet History",
      name: "Bullets",
      balance: bulletBalance,
      link: `/bullet/history`,
      icon: "/assets/imgs/inventory/bulletitem.png",
    },
    {
      tooltip: "Health History",
      name: "Health",
      balance: healthBalance,
      max: 1000,
      link: `/health/history`,
      icon: "/assets/imgs/inventory/healthitem.png",
      showProgress: true,
    },
    {
      tooltip: "Perks",
      name: "Perks",
      balance: `${activePerks.activePerks.length} / 4`,
      link: `/perks`,
      icon: "/assets/imgs/perks/lightning.png",
    },
    {
      tooltip: "Strength",
      name: "Strength",
      balance: myProfile.city
        ? `${citiesTotalPower[myProfile.city]} / ${citiesTotalDefense[myProfile.city]
        }`
        : `0 / 0`,
      link: `/equipment`,
      icon: "/assets/imgs/equipment/arrow_green.png",
      icon2: "/assets/imgs/equipment/arrow_red.png",
    },
  ];

  const weeklyMissionRemainingTime = useMemo(() => {
    return weeklyMissionStatus.startedAt + 7 * 24 * 60 * 60 - currentTime;
  }, [weeklyMissionStatus.startedAt, currentTime]);

  return (
    <Drawer
      PaperProps={{ className: classes.sidebarPaper }}
      open={showMobileSideBar}
      onClose={() => toggleMobileSideBar(false)}
    >
      <Box className={classes.sidebarHeader}>
        <Logo classNames={classes.sidebarLogo} />
        <NavLink to="/"> {config.title}</NavLink>
      </Box>

      {/* Search Bar */}
      <Box className={classes.searchContainer}>
        <TextField
          placeholder="Search..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className={classes.searchInput}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search
                  color={theme.design.color.text.primary}
                  size={16}
                  style={{ opacity: 0.7 }}
                />
              </InputAdornment>
            ),
            endAdornment: searchQuery && (
              <InputAdornment position="end">
                <X
                  onClick={() => setSearchQuery("")}
                  color={theme.design.color.text.primary}
                  size={16}
                  style={{ cursor: "pointer", opacity: 0.7 }}
                />
              </InputAdornment>
            ),
          }}
        />
      </Box>

      {balanceList.map((balance, index) => {
        return (
          <Box
            className={classes.navLinkGroup}
            key={index}
            sx={{ marginBottom: index === balanceList.length - 1 ? 2 : 0 }}
          >
            <Link to={balance.link} className={classes.navLinkHeader}>
              <Box className={classes.navLinkHeaderContent}>
                <Box className={classes.navLinkHeaderContentPart}>
                  <Box
                    component={"img"}
                    src={balance.icon}
                    className={classes.navLinkIcon}
                  />
                  <Typography className={classes.navLinkText}>
                    {balance.name}
                  </Typography>

                  {balance.icon2 && <Box
                    component={"img"}
                    src={balance.icon2}
                    className={classes.navLinkIcon}
                  />}
                </Box>
                {balance.showProgress ? (
                  <Typography className={classes.navLinkText}>
                    {formatNumberWithComma(
                      Math.floor(balance.balance || 0)
                    ) || 0}{" "}
                    {balance.max && `/ ${balance.max}`}
                  </Typography>
                ) : (
                  <Typography className={classes.navLinkText}>
                    {typeof balance.balance === "string"
                      ? balance.balance
                      : formatNumberWithComma(Math.floor(balance.balance || 0))}
                  </Typography>
                )}
              </Box>
            </Link>
          </Box>
        );
      })}

      <Box className={classes.navLinkGroup}>
        <Box className={classes.navLinkHeader}>
          <Box className={classes.navLinkHeaderContent}>
            <ListTodo
              className={classes.navLinkIcon}
              size={20}
              color={theme.design.color.text.secondary}
            />
            <Typography className={classes.navLinkText}>
              Tasks
            </Typography>
          </Box>

          <Box className={classes.navLinkHeaderLine}></Box>
        </Box>

        <Box className={classes.navLinks}>
          <Link to="/story" className={classes.navLink}>
            <Typography className={classes.navLinkText} sx={{ minWidth: "85px" }}>Story mode</Typography>

            <LinearProgress
              variant="determinate"
              value={50}
              sx={{
                height: "6px",
                borderRadius: 8,
                width: "100%",
                backgroundColor: theme.design.color.surface.control,
                "& .MuiLinearProgress-bar": {
                  backgroundColor: theme.design.color.action.primary,
                },
              }}
            />
            <Typography sx={{ fontSize: "10px!important", color: theme.design.color.text.primary, fontWeight: 900, whiteSpace: "nowrap" }}>50%</Typography>

          </Link>
          <Link to="/weekly" className={classes.navLink}>
            <Typography className={classes.navLinkText} sx={{ minWidth: "85px" }}>Missions</Typography>

            <LinearProgress
              variant="determinate"
              value={weeklyMissionProgress}
              sx={{
                height: "6px",
                borderRadius: 8,
                width: "100%",
                backgroundColor: theme.design.color.surface.control,
                "& .MuiLinearProgress-bar": {
                  backgroundColor: theme.design.color.status.warning,
                },
              }}
            />
            <Typography sx={{ fontSize: "8px!important", color: theme.design.color.text.primary, fontWeight: 900, whiteSpace: "nowrap" }}>
              {weeklyMissionRemainingTime > 0 ? convertDateTimeWithMinutes(weeklyMissionRemainingTime) : "Expired"}
            </Typography>
          </Link>
        </Box>
      </Box>

      {filteredNavLinks.map((navLinks, index) => {
        return (
          <Box className={classes.navLinkGroup} key={index}>
            <Box className={classes.navLinkHeader}>
              <Box className={classes.navLinkHeaderContent}>
                <Box
                  component={"img"}
                  src={navLinks.icon}
                  className={classes.navLinkIcon}
                />
                <Typography className={classes.navLinkText}>
                  {navLinks.category === "LOCATION"
                    ? myProfile.city !== undefined
                      ? City[myProfile.city]
                      : "LOCATION"
                    : navLinks.category}
                </Typography>
              </Box>

              <Box className={classes.navLinkHeaderLine}></Box>
            </Box>

            <Box className={classes.navLinks}>
              {navLinks.subLinks.map((link, linkIndex) => {
                if ("action" in link && link.action === "terms") {
                  return (
                    <button
                      type="button"
                      className={`${classes.navLink} ${classes.navAction}`}
                      key={linkIndex}
                      onClick={() => { toggleMobileSideBar(false); openTerms(); }}
                    >
                      <Typography component="span">{link.name}</Typography>
                    </button>
                  );
                }
                const linkPath = navLinks.category === "LOCATION"
                  ? (link as any).needCityId ? link.link + `/${myProfile.city || 0}` : link.link
                  : link.name === "My profile"
                    ? link.link + `/${myProfile.name}`
                    : link.link;
                const isJailRestricted = isInJail && JAIL_RESTRICTED_PATHS.some((path) => linkPath.includes(path));
                const isSafehouseRestricted = isInSafehouse && SAFEHOUSE_RESTRICTED_PATHS.some((path) => linkPath.includes(path));
                const isLinkRestricted = isJailRestricted || isSafehouseRestricted;
                const redirectTo = isJailRestricted ? "/injail" : isSafehouseRestricted ? "/safehouse" : linkPath;

                return (
                  <Link
                    to={redirectTo}
                    className={classes.navLink}
                    key={linkIndex}
                  >
                    <Typography>{link.name}</Typography>

                    <Box className={classes.badgeList}>
                      {(link as any).showCooldown &&
                        remainTimes[link.name].map((time, index) => {
                          return time ? (
                            <Box
                              key={index}
                              className={
                                link.name === "Kill initiation" ||
                                (link.name === "Detective Agency" && time === "FOUND")
                                  ? classes.killCountdownBadge
                                  : link.name === "Vault countdown" ||
                                    (link.name === "Detective Agency" && time === "SEARCHING")
                                  ? classes.vaultCountdownBadge
                                  : time === "Bot"
                                  ? classes.botBadge
                                  : time === "Now"
                                    ? classes.nowBadge
                                    : classes.timeBadge
                              }
                              sx={{
                                backgroundColor:
                                  link.name === "Jail" && time !== "Now"
                                    ? `${theme.design.color.status.danger}!important`
                                    : "",
                              }}
                            >
                              {time ?? "Now"}
                            </Box>
                          ) : (
                            ""
                          );
                        })}
                    </Box>
                  </Link>
                );
              })}
            </Box>
          </Box>
        );
      })}

      <Divider />
      <List>
        {isConnected && (
          <ListItem>
            <ListItemButton className={classes.sidebarNavLink}>
              <NavLink
                className={classes.sidebarNavLink}
                to={`/profile/${myProfile.name}`}
              >
                <Typography component="span" sx={{ fontWeight: 700 }}>
                  {shortAddress(account)}
                </Typography>
                <User
                  size={18}
                  color="currentColor"
                  style={{ marginLeft: 8 }}
                />
              </NavLink>
            </ListItemButton>
          </ListItem>
        )}

        <ListItem>
          <ListItemButton
            className={classes.sideBarConnectWalletBtn}
            onClick={() =>
              isConnected ? disconnectWallet() : onConnectWallet()
            }
          >
            <Typography component="span" sx={{ fontWeight: 700 }}>
              {isConnected ? "Disconnect" : "Connect Wallet"}
            </Typography>
            {isConnected && (
              <Power
                size={18}
                color="currentColor"
                style={{ marginLeft: 8 }}
              />
            )}
          </ListItemButton>
        </ListItem>
      </List>
    </Drawer>
  );
};

export default MobileSideBar;
